import { lazy, useEffect, useRef, useState } from 'react';

// mui components
import { Alert, Box, Button, Typography, useTheme } from '@mui/material';

import { CustomSelect, ModuleList } from './components';
import ConfettiExplosion from 'react-confetti-explosion';
import { PensumsSection } from '@/pages/HomePage/components/widgets/Pensums';
import { CertificatesSection } from '@/pages/HomePage/components/widgets/Certificates';

const AnnounceCertificate = lazy(() =>
  lazyRetry(() => import('./components/AnnounceCertificate')),
);

// hooks and utils
import { useUserRoutes } from '../../hooks';
import { useMetrics } from '../../hooks/useMetrics';
import { useConfig } from '../../hooks/useConfig';

// icons
import { useModules } from '../../hooks/useModules';
import { useTranslation } from 'react-i18next';
import { ContentProgressModal } from './components/ContentProgressModal';
import { useSearchParams } from 'react-router-dom';
import { UnderConstruction } from './components/UnderConstruction';
import { QueryParamsStrings } from '../../constants/queryParamsStrings';
import { capitalize, lazyRetry } from '../../utils/general';
import toast from 'react-hot-toast';
import { CustomLinearProgress } from '@/pages/ListContentPage/components/CustomLinearProgress';
import { COLORS, TYPOGRAPHY } from '@/pages/ListContentPage/constants';
import { getColorProgressCourse } from '@/pages/ListContentPage/utils';
import { FloatingActionButton } from '@/pages/ListContentPage/components/ToUpFAB';
import { getIconStyles, tabButtonSx } from '@/theme/utils';
import { BackButton } from '@/components/BackButton';
import { BookOpen } from 'lucide-react';

export const MainList = () => {
  const [searchParamsFromReactRouter, setSearchParams] = useSearchParams();
  const showProgressModal =
    searchParamsFromReactRouter.get('show_theoretical_modal')?.toLowerCase() ===
    'true';
  //states
  const [activeCourse, setActiveCourse] = useState<number>(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const firstRender = useRef(true);
  const [showUnderConstruction, setShowUnderConstruction] = useState(false);
  const [previousRoute, setPreviousRoute] = useState(0);
  const [activeTab, setActiveTab] = useState<'in_progress' | 'finalized'>(
    'in_progress',
  );
  //hooks
  const { t } = useTranslation();
  const metrics = useMetrics();
  const theme = useTheme();

  // params
  const pensumName = searchParamsFromReactRouter.get(
    QueryParamsStrings.PENSUM_NAME,
  );

  const config = useConfig();
  const {
    routes,
    currentRouteId,
    isLoading: isLoadingRoutes,
    routeFormater,
    getOneRouteInfo,
  } = useUserRoutes();
  const modules = useModules(true);

  const showCerfiticateSection = () => {
    return (
      !config.isMeidei &&
      config.showCertificates &&
      !config.isSimple &&
      activeCourse !== 0
    );
  };

  useEffect(() => {
    if (currentRouteId !== previousRoute) {
      if (currentRouteId && activeCourse === 0) {
        modules.getModules(Number(currentRouteId));
        setActiveCourse(Number(currentRouteId));
      } else {
        modules.getModules(!!activeCourse ? activeCourse : currentRouteId);
      }
      setPreviousRoute(currentRouteId);
    }
  }, [currentRouteId, activeCourse, routes]);

  useEffect(() => {
    const routeInfo = getOneRouteInfo(currentRouteId);
    const canShowUnderConstruction =
      routeInfo.progressPercentage === 100 && routeInfo.finished === false;
    if (canShowUnderConstruction) {
      setShowUnderConstruction(true);
    }
  }, [currentRouteId]);

  useEffect(() => {
    if (Boolean(currentRouteId)) {
      if (
        searchParamsFromReactRouter.get(QueryParamsStrings.SELECTED_ROUTE) ===
        null
      ) {
        setSearchParams({
          ...Object.fromEntries(searchParamsFromReactRouter),
          selected_route: String(currentRouteId),
        });
      }
    }
  }, [routes]);

  useEffect(() => {
    if (searchParamsFromReactRouter.get('party')?.toLowerCase() === 'true') {
      if (!firstRender.current) return;
      setShowConfetti(true);
      toast.success(
        `${t('learningPathScreen.congratulations')}. ${t(
          'contentScreen.upToDate',
        )}`,
        {
          icon: <>🎉</>,
        },
      );
      firstRender.current = false;
    }
  }, [searchParamsFromReactRouter.get('party')]);

  function countHighProgressModules(modules: any) {
    return modules.filter((module: any) => {
      // Si el valor es null, se trata como 0
      const progress =
        module.progress_percentage === null ? 0 : module.progress_percentage;
      return progress >= 90;
    }).length;
  }

  if (currentRouteId === 0) {
    return <></>;
  }

  const selectedRoute = routes.find(
    (r) =>
      r.route.pk ===
      Number(
        searchParamsFromReactRouter.get(QueryParamsStrings.SELECTED_ROUTE),
      ),
  );
  const courseName = selectedRoute?.route.name ?? '';
  const courseProgress = Number(selectedRoute?.progress_percentage ?? 0);

  const formattedModules = modules.listModulesFormatter(modules.data);
  const inProgressModules = formattedModules.filter(
    (m) => m.progressPercentage !== 100,
  );
  const finalizedModules = formattedModules.filter(
    (m) => m.progressPercentage === 100,
  );
  const filteredModules =
    activeTab === 'in_progress' ? inProgressModules : finalizedModules;

  return (
    <div style={{ position: 'relative' }}>
      {showConfetti && (
        <div
          style={{
            width: '100vw',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <ConfettiExplosion />
          </div>
          <div>
            <ConfettiExplosion />
          </div>
        </div>
      )}
      {showProgressModal && <ContentProgressModal />}
      {showUnderConstruction && (
        <UnderConstruction
          setOpen={setShowUnderConstruction}
          open={showUnderConstruction}
        />
      )}
      <Box maxWidth={{ sm: '100%', md: '70%' }} marginX={'auto'}>
        <BackButton />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 3,
            mt: 1,
          }}
        >
          <Box sx={getIconStyles(theme.palette.primary.main)}>
            <BookOpen size={20} color={theme.palette.primary.main} />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: theme.fontSizes.title,
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t('homeScreen.myCourses')}
          </Typography>
        </Box>

        {pensumName && (
          <Typography
            sx={{
              fontFamily: TYPOGRAPHY.bold,
              fontSize: theme.fontSizes.body,
              mb: 1,
              textTransform: 'capitalize',
            }}
          >
            {capitalize(pensumName)}
          </Typography>
        )}

        {/* Course header: responsive layout - stacked on mobile, side-by-side on desktop */}
        {!modules.isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: { md: 'flex-start' },
              mt: 1,
            }}
          >
            {/* Progress info */}
            <Box flex={1}>
              {courseName && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: { md: 'space-between' },
                    alignItems: 'start',
                    gap: { xs: 0.5, md: 0 },
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: TYPOGRAPHY.bold,
                      fontSize: theme.fontSizes.title,
                      color: theme.palette.text.primary,
                      fontWeight: 700,
                    }}
                  >
                    {courseName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: theme.fontSizes.caption,
                      color: theme.palette.text.secondary,
                      flexShrink: 0,
                    }}
                  >
                    {countHighProgressModules(modules.data)}/
                    {modules.data.length} {t('general.courseModulesCompleted')}
                  </Typography>
                </Box>
              )}
              <CustomLinearProgress
                progress={courseProgress}
                colorTrack={COLORS.GRAY_EXTRA_LIGHT}
                colorBar={getColorProgressCourse(courseProgress)}
              />
            </Box>

            {/* Dropdown selector - top on mobile (order: -1), right on desktop */}
            {!config.isSimple && modules.data.length > 0 && (
              <Box
                sx={{
                  minWidth: { md: '260px' },
                  order: { xs: -1, md: 0 },
                  mt: { xs: 0, md: 0.5 },
                  maxWidth: { md: '40%' },
                }}
              >
                <CustomSelect
                  label={
                    config.isMeidei
                      ? t('general.courseMeidei')
                      : config.isCustom
                      ? config.isCustom
                      : t('general.course')
                  }
                  onChange={(id: any) => {
                    setActiveCourse(id);
                    modules.getModules(id);
                    setActiveTab('in_progress');
                    setSearchParams({
                      ...Object.fromEntries(searchParamsFromReactRouter),
                      selected_route: String(id),
                    });
                    metrics.track({
                      action: 'click-select',
                      extra_data: {
                        button: metrics.MetricsKeys.SELECT_MODULE,
                        path: '/content',
                      },
                    });
                  }}
                  options={routeFormater(routes)}
                  currentValue={
                    Boolean(activeCourse) ? activeCourse : currentRouteId
                  }
                />
              </Box>
            )}
          </Box>
        )}

        {/* Empty state */}
        {!isLoadingRoutes && routes.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('contentScreen.noActiveCourse')}
          </Alert>
        )}

        {/* Tab filter */}
        {formattedModules.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, md: 2 },
              mt: 2.5,
              mb: 2,
            }}
          >
            <Button
              size="small"
              onClick={() => setActiveTab('in_progress')}
              sx={tabButtonSx(activeTab === 'in_progress', theme)}
            >
              {t('contentPageNew.inProgress')} ({inProgressModules.length})
            </Button>
            <Button
              size="small"
              onClick={() => setActiveTab('finalized')}
              sx={tabButtonSx(activeTab === 'finalized', theme)}
            >
              {t('contentPageNew.finalized')} ({finalizedModules.length})
            </Button>
          </Box>
        )}

        {/* Module list */}
        {modules.data.length > 0 && (
          <ModuleList isLoading={modules.isLoading} modules={filteredModules} />
        )}

        {showCerfiticateSection() && (
          <AnnounceCertificate
            completedModules={modules.getCompletedModulesCount()}
            totalModules={modules.metadata?.count}
            courseTitle={
              getOneRouteInfo(
                activeCourse === 0 ? currentRouteId : activeCourse,
              )?.name ?? ''
            }
          />
        )}

        {/* Mobile-only: Pensums + Certificates quick links */}
        {!config.isSimple && (
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              gap: 1.5,
              mt: 3,
              mb: 2,
            }}
          >
            <PensumsSection />
            {config.showCertificates && <CertificatesSection />}
          </Box>
        )}
      </Box>
      {modules.data.length > 0 && <FloatingActionButton />}
    </div>
  );
};

export default MainList;
